import React, { useMemo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { isEqual } from "lodash";
import * as actionTypes from "./actionTypes";
import providerPanelsReducer, { reducerInitialState } from "./providerPanelsReducer";
import { apolloClient } from "@td/ui";
import PROVIDER_PANELS_QUERY from "../../queries/provider-panels.graphql";
import { applyQueryString, filtersMapper, parseQueryString } from "../../utils";

const ProviderPanelsContext = React.createContext();

const ProviderPanelsProvider = ({ specialtyCode, enableStoredPreferences, ...props }) => {
  const [state, dispatch] = React.useReducer(providerPanelsReducer, reducerInitialState);

  const availableFilters = filtersMapper.getAllowedFiltersBySpecialty(specialtyCode);

  const fetchProviderPanels = useCallback(() => {
    const { pageParams, filters } = state;

    dispatch({ type: actionTypes.FETCH_PROVIDER_PANELS_PENDING });

    apolloClient.query({
      query: PROVIDER_PANELS_QUERY,
      variables: {
        ...pageParams,
        filters: {
          ...filtersMapper.toRequestParams(filters, availableFilters),
          specialtyCode,
        },
      },
      fetchPolicy: "network-only",
    }).then(({ data: { providerPanelsSearch: { items: providerPanels, page, perPage, totalCount: total }}}) => {
      dispatch({
        type: actionTypes.FETCH_PROVIDER_PANELS_SUCCEEDED,
        payload: { providerPanels, meta: { page, perPage, total }},
      });
    }).catch((error) => {
      dispatch({
        type: actionTypes.FETCH_PROVIDER_PANELS_FAILED,
        payload: { error },
      });
    });

    const expectedQueryParams = {
      ...filtersMapper.toQueryParams(filters),
      ...pageParams,
      specialty: specialtyCode,
    };

    if (!isEqual(parseQueryString(), expectedQueryParams)) {
      applyQueryString(expectedQueryParams);
    }
  }, [state, dispatch]);

  const updatePageParams = useCallback((payload) => {
    dispatch({ type: actionTypes.UPDATE_PAGE_PARAMS, payload });
  }, [dispatch]);

  const updateFilters = useCallback((payload) => {
    dispatch({ type: actionTypes.UPDATE_FILTERS, payload });
  }, [dispatch]);

  const resetFilters = useCallback(() => {
    dispatch({ type: actionTypes.RESET_FILTERS });
  }, [dispatch]);

  const initializeComponent = useCallback((payload) => {
    dispatch({ type: actionTypes.INITIALIZE_COMPONENT, payload });
  }, [dispatch]);

  return (
    <ProviderPanelsContext.Provider
      {...props}
      value={{
        ...state,
        fetchProviderPanels,
        updatePageParams,
        updateFilters,
        resetFilters,
        initializeComponent,
        specialtyCode,
        enableStoredPreferences,
      }}
    />
  );
};

ProviderPanelsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useProviderPanels = () => {
  const context = useContext(ProviderPanelsContext);

  if (context === undefined) {
    throw new Error("useProviderPanels must be used within a ProviderPanelsProvider");
  }

  return context;
};

export default ProviderPanelsProvider;
